import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Calculator, ChevronRight, FileText, Filter, Search, Send, Trophy, XCircle } from 'lucide-react';

const STAGE_STYLE = {
  new: { icon: FileText, chip: 'bg-blue-50 text-blue-600', bar: 'bg-blue-500' },
  feasibility: { icon: Search, chip: 'bg-violet-50 text-violet-600', bar: 'bg-violet-500' },
  costing: { icon: Calculator, chip: 'bg-amber-50 text-amber-600', bar: 'bg-amber-500' },
  quoted: { icon: Send, chip: 'bg-blue-50 text-blue-600', bar: 'bg-blue-600' },
  won: { icon: Trophy, chip: 'bg-emerald-50 text-emerald-600', bar: 'bg-emerald-500' },
  lost: { icon: XCircle, chip: 'bg-slate-100 text-slate-400', bar: 'bg-slate-400' },
};

/** Inquiry pipeline — each stage is a hit area into the filtered register. */
export const InquiryFunnelStrip = ({ funnel }) => {
  const navigate = useNavigate();
  const stages = funnel?.stages || [];
  const peak = Math.max(1, ...stages.map((stage) => stage.count));

  return (
    <section
      className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_8px_24px_-18px_rgba(16,24,40,0.16)]"
      data-testid="inquiry-funnel-strip"
    >
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
          <Filter className="h-4 w-4 text-blue-600" strokeWidth={1.75} /> Inquiry pipeline
        </h2>
        <p className="flex flex-wrap items-center gap-x-2 text-xs text-slate-500">
          <span className="h-2 w-2 rounded-full bg-blue-500" />
          <span className="font-mono font-semibold tabular-nums text-slate-800">{funnel?.total || 0}</span> inquiries
          <span className="ml-2 font-mono font-semibold tabular-nums text-slate-800">{funnel?.quote_rate || 0}%</span> reached a quote
          <span className="ml-2 h-2 w-2 rounded-full bg-emerald-500" />
          <span className="font-mono font-semibold tabular-nums text-emerald-600">{funnel?.win_rate || 0}%</span> won of quoted
        </p>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        {stages.map((stage) => {
          const style = STAGE_STYLE[stage.key] || STAGE_STYLE.lost;
          const Icon = style.icon;
          return (
            <button
              key={stage.key}
              type="button"
              onClick={() => navigate(`/inquiries?status=${stage.statuses[0]}`)}
              className="group rounded-xl border border-slate-200/70 bg-slate-50/60 p-4 text-left transition-colors duration-200 hover:border-blue-200 hover:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/40"
              data-testid={`funnel-stage-${stage.key}`}
            >
              <div className="flex items-center gap-3">
                <span className={`flex h-9 w-9 items-center justify-center rounded-lg ${style.chip}`}>
                  <Icon className="h-4 w-4" strokeWidth={1.75} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="font-mono text-2xl font-bold leading-none tabular-nums text-slate-900">{stage.count}</p>
                  <p className="mt-1 truncate text-xs text-slate-500">{stage.label}</p>
                </div>
                <ChevronRight className="h-4 w-4 shrink-0 text-slate-300 transition-colors group-hover:text-blue-500" strokeWidth={1.75} />
              </div>
              <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-slate-200/80">
                <div className={`h-full rounded-full ${style.bar}`} style={{ width: `${Math.round((stage.count / peak) * 100)}%` }} />
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
};
